import React, { useCallback } from 'react';

import CardPricing, { CardPricingProps } from '../CardPricing';
import CardContent, { CardContentProps } from '../CardContent';
import CardValue, { CardValueProps } from '../CardValue';
import CardMediaSmall, { CardMediaSmallProps } from '../CardMediaSmall';
import CardNavigationMedia, {
    CardNavigationMediaProps,
} from '../CardNavigationMedia';
import CardNavigationText, {
    CardNavigationTextProps,
} from '../CardNavigationText';
import CardIcon, { CardIconProps } from '../CardIcon';
import CardMediaLarge, { CardMediaLargeProps } from '../CardMediaLarge';
import CardTestimonial, { CardTestimonialProps } from '../CardTestimonial';
import CardPodcast, { CardPodcastProps } from '../CardPodcast';

export type DynamicCardProps<
    T =
        | CardPricingProps
        | CardContentProps
        | CardValueProps
        | CardMediaSmallProps
        | CardNavigationMediaProps
        | CardNavigationTextProps
        | CardIconProps
        | CardMediaLargeProps
        | CardTestimonialProps
        | CardPodcastProps,
> = {
    cardType:
        | 'pricing'
        | 'content'
        | 'value'
        | 'mediaSmall'
        | 'navigationMedia'
        | 'navigationText'
        | 'iconLarge'
        | 'iconSmall'
        | 'mediaLarge'
        | 'testimonial'
        | 'podcast';
    showOnlyXs?: boolean;
    showOnLg?: boolean;
} & T;

const Cards: Record<DynamicCardProps['cardType'], React.ElementType> = {
    pricing: CardPricing,
    content: CardContent,
    value: CardValue,
    mediaSmall: CardMediaSmall,
    navigationMedia: CardNavigationMedia,
    navigationText: CardNavigationText,
    iconLarge: CardIcon,
    iconSmall: CardIcon,
    mediaLarge: CardMediaLarge,
    testimonial: CardTestimonial,
    podcast: CardPodcast,
};

const DynamicCard = ({ cardType, ...item }: DynamicCardProps) => {
    const formattedItem = useCallback(
        (
            item: Omit<DynamicCardProps, 'cardType'>,
            cardType: DynamicCardProps['cardType'],
        ) => {
            switch (cardType) {
                case 'iconLarge':
                    return {
                        ...item,
                        variant: 'large',
                    };
                case 'iconSmall':
                    return {
                        ...item,
                        variant: 'small',
                    };
                default:
                    return item;
            }
        },
        [],
    );

    if (typeof Cards[cardType] !== 'undefined') {
        const Card = Cards[cardType];

        return <Card {...formattedItem(item, cardType)} />;
    }

    return null;
};

export default DynamicCard;
